import { useCallback, useEffect, useState } from "react";
import { mcpCall } from "./mcp-client";

/**
 * Per-turn carrier metadata for chips + agent-graph labels.
 *
 * Reads /mcp/settlement.list_adapter once on mount so the frontend
 * knows whether to render PayPay-only chips (SoftBank deploy) or
 * the JPYC/BTC/ETH chip set (KDDI deploy). Falls back to a
 * paypay_402 / yen-only shape if the route is missing.
 */
export interface CarrierContext {
  adapterId: string;                  // "paypay_402" | "coincheck_spot" | …
  supportedDenominations: string[];
  carrierId: string;
  settlementBrand: string;
  loyaltyProgramName: string;
  loyaltyBalance: number | null;       // null while loading / errored
  loyaltyUnitsLabel: string;
}

const FALLBACK: CarrierContext = {
  adapterId: "paypay_402",
  supportedDenominations: ["yen"],
  carrierId: "paypay_softbank",
  settlementBrand: "PayPay",
  loyaltyProgramName: "PayPay Points",
  loyaltyBalance: null,
  loyaltyUnitsLabel: "points",
};


export function useCarrierContext(consumerId: string): CarrierContext {
  const [ctx, setCtx] = useState<CarrierContext>(FALLBACK);

  const refresh = useCallback(async () => {
    try {
      type SettlementListAdapterResp = {
        adapter_id: string;
        supported_denominations: string[];
        carrier_id: string;
        settlement_brand: string;
      };
      const settlement = await mcpCall<SettlementListAdapterResp>(
        "settlement.list_adapter",
        {},
        { service: "aide" },
      );
      let loyalty = {
        program_name: FALLBACK.loyaltyProgramName,
        balance_units: 0,
        units_label: FALLBACK.loyaltyUnitsLabel,
      };
      try {
        type LoyaltyResp = {
          balance: {
            program_name: string;
            balance_units: number;
            units_label: string;
          };
        };
        const lb = await mcpCall<LoyaltyResp>(
          "loyalty.list_balance",
          { consumer_id: consumerId },
          { service: "aide" },
        );
        loyalty = lb.balance;
      } catch {
        // Loyalty fetch failure → fall through with zero balance;
        // the chip will render in a "no balance" state.
      }
      setCtx({
        adapterId: settlement.adapter_id,
        supportedDenominations: settlement.supported_denominations,
        carrierId: settlement.carrier_id,
        settlementBrand: settlement.settlement_brand,
        loyaltyProgramName: loyalty.program_name,
        loyaltyBalance: loyalty.balance_units,
        loyaltyUnitsLabel: loyalty.units_label,
      });
    } catch {
      setCtx(FALLBACK);
    }
  }, [consumerId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return ctx;
}
